import { SalusMark } from "./salus-mark.tsx";

import type { FC } from "react";

// Brand lockup of this app. Same build as the kit's `<KoruLogo>` (symbol +
// wordmark, font-size and gap derived from `size` through fixed ratios), with the
// "KORU Salus" wordmark and the symbol in the Salus colours (`<SalusMark>`): the
// kit exposes neither the text nor the gradient stops as props — it takes an image
// `src` and nothing else — so until a customer asset exists the lockup is
// redeclared here instead of forking the design-system.
//
// A DERIVED APP replaces both surfaces with its own mark through `createApp`
// (`branding.publicLogo`, `branding.logo`), the way Esseci does: this lockup is
// the PRODUCT's, the one an install with no customer branding shows.
const FONT_RATIO = 0.57;
const GAP_RATIO = 0.33;

export interface SalusLogoProps {
  /** Height of the symbol in px. Defaults to 58 (the login card). */
  size?: number;
  /**
   * Tone of the wordmark:
   * - `ink` (default): fixed ink/grey hex values → the PUBLIC views (login, reset,
   *   404/403), which are light-only and do NOT follow the dark mode;
   * - `theme`: the `foreground`/`muted-foreground` tokens → the contexts that do
   *   follow it (the About page, inside the authenticated shell).
   */
  tone?: "ink" | "theme";
}

export const SalusLogo: FC<SalusLogoProps> = ({ size = 58, tone = "ink" }) => {
  const fontSize = Math.round(size * FONT_RATIO);
  const gap = Math.round(size * GAP_RATIO);
  const themed = tone === "theme";
  return (
    <div className="flex items-center" style={{ gap: `${gap}px` }}>
      <span
        className="block flex-none"
        style={{ width: `${size}px`, height: `${size}px`, lineHeight: 0 }}
        aria-hidden="true"
      >
        <SalusMark variant="gradient" className="block h-full w-full" />
      </span>
      <span
        className="whitespace-nowrap font-sans"
        style={{ fontSize: `${fontSize}px`, lineHeight: 1, letterSpacing: "-0.02em" }}
      >
        <span
          className={themed ? "text-foreground" : undefined}
          style={{ fontWeight: 800, color: themed ? undefined : "#16121D" }}
        >
          KORU
        </span>{" "}
        <span
          className={themed ? "text-muted-foreground" : undefined}
          style={{ fontWeight: 500, color: themed ? undefined : "#6B7280" }}
        >
          Salus
        </span>
      </span>
    </div>
  );
};
